import {
  type ConfigureField,
  type ConfigureSpec,
  type ConnectedSource,
} from "@/shadcn/components/NewAutomation/types"

/**
 * The new-design configure form for the three OAuth social inputs — a Facebook
 * page, an Instagram professional account, a TikTok account.
 *
 * One module for all three because prod's three panels are near-identical
 * copies of each other: the same `attrs.typeFilter` bitmask, the same
 * `socialOverrides` pair, and copy that differs only in the word for a post.
 * Adding a fourth copy per panel is how the three legacy files ended up
 * disagreeing in small ways already (TikTok's panel shows Facebook's help text
 * and a "Add Facebook Page Name" placeholder).
 *
 * NO FIGMA FRAME EXISTS for these three. The six drawn configure frames are
 * YouTube, Pinterest, Etsy, Shopify, Substack and WordPress — see
 * CONFIGURE_BY_SOURCE. This follows YouTube's frame: prod's own field labels
 * verbatim, YouTube's segmented toggle for a multi-option choice, and the
 * account chip in place of SocialInputConfigWidget. Confirm with the designer
 * before shipping.
 */

type Constants = Record<string, number>

/** The input record's fields this file reads. Legacy data, so all optional. */
export type SocialInputRecord = {
  name?: string
  attrs?: { typeFilter?: number | string } | null
  socialOverrides?: {
    postStyle?: number | string
    postText?: number | string
  } | null
}

export type SocialConfigureNetwork = "facebook" | "instagram" | "tiktok"

/**
 * Field names are dispatch keys, NOT `data-field` paths.
 *
 * Unlike the other configure panels, two of these fields write to the SAME
 * backing field by different arithmetic — `postTypes` flips a source bit,
 * `feedMedia` swaps the media bits, and both land in `attrs.typeFilter`. A path
 * per field could not tell them apart, so the panel maps these keys to paths
 * itself.
 */
export const SOCIAL_CONFIGURE_FIELDS = {
  postTypes: "postTypes",
  feedMedia: "feedMedia",
  postStyle: "postStyle",
  postText: "postText",
} as const

const OVERRIDE_PATH: Record<string, string> = {
  postStyle: "socialOverrides.postStyle",
  postText: "socialOverrides.postText",
}

type NetworkCopy = {
  account: ConnectedSource
  /** Whether the network exposes the feed / reels / stories filter. */
  postTypes: boolean
  /** postText labels in SOC_OVRD_TEXT_BOTH, _TEXT_CAPTION, _NONE order. */
  textLabels: [string, string, string]
  styleHelp: string
  textHelp: string
}

/**
 * Per-network copy. The labels are prod's legend text verbatim, including the
 * trailing colons, which prod uses on these three but not on YouTube's "What
 * text should we include?".
 *
 * `styleHelp` and `textHelp` condense the legacy HelpButton popovers
 * (`fbInputNativeMedia`, `igInputCaption`, …) into the one muted line the design
 * gives a field. The popovers explained each option in a paragraph; these say
 * the same thing in a sentence, which is the trade the design makes.
 */
const NETWORK_COPY: Record<SocialConfigureNetwork, NetworkCopy> = {
  facebook: {
    account: {
      name: "Facebook",
      meta: "Facebook Page",
      monogram: "F",
      network: "facebook",
    },
    postTypes: true,
    textLabels: ["Message + Hashtags", "Message", "No Text"],
    styleHelp:
      "Native media shows photos and videos in full; adding Link also links back to the original post. Shared Link posts a link card instead.",
    textHelp:
      "Message + Hashtags keeps both, Message drops the hashtags, No Text posts the media on its own.",
  },
  instagram: {
    account: {
      name: "Instagram",
      meta: "Instagram Account",
      monogram: "I",
      network: "instagram",
    },
    postTypes: true,
    textLabels: ["Caption + Hashtags", "Caption", "No Text"],
    styleHelp:
      "Native media shows photos and videos in full; adding Link also links back to the original post. Shared Link posts a link card instead.",
    textHelp:
      "Caption + Hashtags keeps both, Caption drops the hashtags, No Text posts the media on its own.",
  },
  tiktok: {
    account: {
      name: "TikTok",
      meta: "TikTok Account",
      monogram: "T",
      network: "tiktok",
    },
    // TikTok returns videos only, so prod's panel has the feed/reels/stories
    // block commented out rather than absent — same conclusion, kept here.
    postTypes: false,
    textLabels: ["Description + Hashtags", "Description", "No Text"],
    // Prod's TikTok panel points at Facebook's help copy, which names Facebook
    // in all three paragraphs. Written network-neutral here instead.
    styleHelp:
      "Native media shows the video in full; adding Link also links back to the original post. Shared Link posts a link card instead.",
    textHelp:
      "Description + Hashtags keeps both, Description drops the hashtags, No Text posts the video on its own.",
  },
}

function toInt(value: number | string | null | undefined): number {
  return parseInt(String(value ?? ""), 10) || 0
}

function typeFilterOf(input: SocialInputRecord | null | undefined): number {
  return toInt(input?.attrs?.typeFilter)
}

/**
 * Whether the "Feed Post Media:" field applies.
 *
 * Prod hides it once reels or stories are in the mix, because those are always
 * video and the image/video bits stop describing anything the user chose.
 */
export function showsFeedMedia(
  input: SocialInputRecord | null | undefined,
  constants: Constants
): boolean {
  const typeFilter = typeFilterOf(input)

  return (
    !(typeFilter & constants.INPUT_SOURCE_FILTER_REELS) &&
    !(typeFilter & constants.INPUT_SOURCE_FILTER_STORIES)
  )
}

/** The media field's current value, or undefined when neither bit is set. */
function feedMediaValue(
  typeFilter: number,
  constants: Constants
): string | undefined {
  const image = typeFilter & constants.INPUT_TYPE_FILTER_IMAGE
  const video = typeFilter & constants.INPUT_TYPE_FILTER_VIDEO

  if (image && video) {
    return String(
      constants.INPUT_TYPE_FILTER_IMAGE + constants.INPUT_TYPE_FILTER_VIDEO
    )
  }
  if (image) return String(constants.INPUT_TYPE_FILTER_IMAGE)
  if (video) return String(constants.INPUT_TYPE_FILTER_VIDEO)

  return undefined
}

export function socialConfigureSpec(
  network: SocialConfigureNetwork,
  input: SocialInputRecord | null | undefined,
  constants: Constants
): ConfigureSpec {
  const copy = NETWORK_COPY[network]
  const typeFilter = typeFilterOf(input)
  const overrides = input?.socialOverrides
  const fields: ConfigureField[] = []

  if (copy.postTypes) {
    const selected: string[] = []
    for (const bit of [
      constants.INPUT_SOURCE_FILTER_FEED,
      constants.INPUT_SOURCE_FILTER_REELS,
      constants.INPUT_SOURCE_FILTER_STORIES,
    ]) {
      if (typeFilter & bit) selected.push(String(bit))
    }

    fields.push({
      label: "Post the following items:",
      name: SOCIAL_CONFIGURE_FIELDS.postTypes,
      kind: "checkbox",
      options: [
        { value: String(constants.INPUT_SOURCE_FILTER_FEED), label: "Feed Posts" },
        { value: String(constants.INPUT_SOURCE_FILTER_REELS), label: "Reels" },
        { value: String(constants.INPUT_SOURCE_FILTER_STORIES), label: "Stories" },
      ],
      values: selected,
    })

    if (showsFeedMedia(input, constants)) {
      fields.push({
        label: "Feed Post Media:",
        name: SOCIAL_CONFIGURE_FIELDS.feedMedia,
        kind: "segmented",
        options: [
          {
            value: String(
              constants.INPUT_TYPE_FILTER_IMAGE +
                constants.INPUT_TYPE_FILTER_VIDEO
            ),
            label: "Images + Videos",
          },
          { value: String(constants.INPUT_TYPE_FILTER_IMAGE), label: "Images" },
          { value: String(constants.INPUT_TYPE_FILTER_VIDEO), label: "Videos" },
        ],
        value: feedMediaValue(typeFilter, constants),
        // Worth saying, because this field disappears when reels or stories are
        // ticked and nothing else on screen explains why.
        help: "Applies to feed posts only.",
      })
    }
  }

  fields.push({
    label: "Post photos & videos as:",
    name: SOCIAL_CONFIGURE_FIELDS.postStyle,
    kind: "segmented",
    options: [
      { value: String(constants.SOC_OVRD_STYLE_MEDIA), label: "Native Media" },
      {
        value: String(constants.SOC_OVRD_STYLE_BOTH),
        label: "Native Media + Link",
      },
      { value: String(constants.SOC_OVRD_STYLE_LINK), label: "Shared Link" },
    ],
    value: String(
      overrides?.postStyle !== undefined
        ? overrides.postStyle
        : constants.SOC_OVRD_STYLE_MEDIA
    ),
    help: copy.styleHelp,
  })

  fields.push({
    label: "Include text in posts:",
    name: SOCIAL_CONFIGURE_FIELDS.postText,
    kind: "segmented",
    options: [
      { value: String(constants.SOC_OVRD_TEXT_BOTH), label: copy.textLabels[0] },
      {
        value: String(constants.SOC_OVRD_TEXT_CAPTION),
        label: copy.textLabels[1],
      },
      { value: String(constants.SOC_OVRD_NONE), label: copy.textLabels[2] },
    ],
    value: String(
      overrides?.postText !== undefined
        ? overrides.postText
        : constants.SOC_OVRD_TEXT_BOTH
    ),
    help: copy.textHelp,
  })

  return {
    account: { ...copy.account, name: input?.name || copy.account.name },
    fields,
  }
}

/**
 * What `ConfigureStep`'s `onFieldChange` needs to reach the panel's own
 * handlers.
 *
 * `onPostTypes` is the panel's existing `onPostTypesClick`, taken as a callback
 * rather than reimplemented: the bitmask arithmetic it does — clearing the media
 * bits before adding one, refusing to untick the last source, re-adding both
 * media bits when the filter falls back to feed-only — is the part that must not
 * drift between the legacy panel and this one.
 */
export type SocialConfigureContext = {
  statePath?: string
  onPostTypes: (event: unknown) => void
  onChange?: (event: unknown) => void
}

/** An event shaped the way `FormUtilityMixin.requestChange` reads one. */
function changeEvent(field: string, value: number): unknown {
  return {
    currentTarget: { dataset: { field } },
    target: { value },
  }
}

/**
 * Routes one configure-step change to the panel's handlers.
 *
 * Note what does NOT happen here: the legacy `onPostStyleClick` is bypassed.
 * All it adds over a plain change is unchecking its sibling radios by ref, which
 * it only needs because those radios share no `name` attribute — and in this
 * mode they aren't rendered, so calling it would dereference null refs. Both a
 * single-value toggle group and a select are single-select by construction.
 */
export function socialConfigureChange(
  field: ConfigureField,
  value: string,
  context: SocialConfigureContext
): void {
  const statePath = context.statePath ?? "input"

  switch (field.name) {
    case SOCIAL_CONFIGURE_FIELDS.postTypes:
      // The clicked option, plus whether it is about to become ticked. The
      // handler reads `checked` the way a real checkbox event carries it.
      context.onPostTypes({
        target: {
          nodeName: "INPUT",
          dataset: { option: value },
          checked: !(field.values ?? []).includes(value),
        },
      })
      break

    case SOCIAL_CONFIGURE_FIELDS.feedMedia:
      // nodeName SELECT is what sends the handler down its replace-the-media-
      // bits path instead of its add-or-remove-a-source-bit path. It describes
      // the path taken, not the control rendered — this field is a segmented
      // toggle on desktop.
      context.onPostTypes({
        target: { nodeName: "SELECT", value: value, dataset: {} },
      })
      break

    case SOCIAL_CONFIGURE_FIELDS.postStyle:
    case SOCIAL_CONFIGURE_FIELDS.postText:
      if (context.onChange) {
        // Both hold numeric SOC_OVRD_* constants, and every control here
        // round-trips its option values as strings. Without the cast back, the
        // stored value stops matching the constants everything else compares it
        // against.
        context.onChange(
          changeEvent(
            `${statePath}.${OVERRIDE_PATH[field.name]}`,
            parseInt(value, 10)
          )
        )
      }
      break

    default:
      break
  }
}
